import { useState } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { usePillbox, SCHEDULES, type Difficulty, type Schedule, type Medicine, type Bottle, type PlacedPill } from './usePillbox'
import { type PillShape } from './pillboxData'
import { useTimer } from '../useTimer'
import GameSetupScreen from '../GameSetupScreen'
import SaveErrorBanner from '../SaveErrorBanner'
import { useMobile } from '../../hooks/useMobile'
import { useSessionMode } from '../../professional/SessionModeContext'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'
import GameCompleteScreen from '../GameCompleteScreen'
import { formatGameTime } from '../utils'
import GamePlayHeader from '../GamePlayHeader'
import { COLORS } from '../../styles/tokens'

type GameScreen = 'setup' | 'playing' | 'complete'

interface PillboxConfig { difficulty: Difficulty }

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

const SCHEDULE_ICONS: Record<Schedule, string> = {
  breakfast: '🌅',
  lunch:     '☀️',
  merienda:  '🌤️',
  dinner:    '🌙',
}

const COMPARTMENT_THEME: Record<Schedule, { header: string; headerText: string; body: string; border: string }> = {
  breakfast: { header: '#F59E0B', headerText: '#FFFFFF', body: '#FFFDF5', border: '#F59E0B' },
  lunch:     { header: '#3B82F6', headerText: '#FFFFFF', body: '#F5F9FF', border: '#3B82F6' },
  merienda:  { header: '#10B981', headerText: '#FFFFFF', body: '#F5FFFC', border: '#10B981' },
  dinner:    { header: '#8B5CF6', headerText: '#FFFFFF', body: '#FAF5FF', border: '#8B5CF6' },
}

// ── Pill shape SVG ─────────────────────────────────────────────

function PillSVG({ shape, color, size = 24 }: { shape: PillShape; color: string; size?: number }) {
  switch (shape) {
    case 'circle':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="10" fill={color} />
          <circle cx="12" cy="12" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="1.5" />
        </svg>
      )
    case 'oval':
      return (
        <svg width={Math.round(size * 1.6)} height={size} viewBox="0 0 40 24" aria-hidden="true">
          <ellipse cx="20" cy="12" rx="18" ry="10" fill={color} />
          <ellipse cx="20" cy="12" rx="18" ry="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="1.5" />
        </svg>
      )
    case 'capsule':
      return (
        <svg width={Math.round(size * 0.65)} height={Math.round(size * 1.6)} viewBox="0 0 18 28" aria-hidden="true">
          <rect x="2" y="7" width="14" height="14" fill={color} />
          <ellipse cx="9" cy="7"  rx="7" ry="7" fill={color} />
          <ellipse cx="9" cy="21" rx="7" ry="7" fill={color} />
          <ellipse cx="9" cy="7"  rx="7" ry="7" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="1.5" />
          <ellipse cx="9" cy="21" rx="7" ry="7" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="1.5" />
          <rect x="2" y="7" width="14" height="14" fill="none" />
        </svg>
      )
    case 'tablet':
      return (
        <svg width={Math.round(size * 1.6)} height={Math.round(size * 0.65)} viewBox="0 0 40 18" aria-hidden="true">
          <rect x="1" y="1" width="38" height="16" rx="8" fill={color} />
          <rect x="1" y="1" width="38" height="16" rx="8" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="1.5" />
          <line x1="20" y1="1" x2="20" y2="17" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
        </svg>
      )
  }
}

// ── Bottle card ────────────────────────────────────────────────

function BottleCard({ bottle, isSelected, onSelect }: {
  bottle:     Bottle
  isSelected: boolean
  onSelect:   () => void
}) {
  return (
    <button
      className={isSelected ? 'pb-selected' : undefined}
      style={{
        ...s.bottle,
        borderColor: isSelected ? bottle.pillColor : '#D8D0C4',
        boxShadow:   isSelected ? `0 0 0 3px ${bottle.pillColor}50, 0 4px 12px rgba(0,0,0,0.1)` : '0 2px 8px rgba(0,0,0,0.07)',
        background:  isSelected ? `${bottle.pillColor}10` : '#FFFFFF',
      }}
      onClick={(e) => { e.stopPropagation(); onSelect() }}
      aria-label={bottle.name}
      aria-pressed={isSelected}
    >
      {/* Bottle cap */}
      <div style={{ ...s.bottleCap, background: bottle.pillColor }} />
      {/* Bottle neck */}
      <div style={s.bottleNeck} />
      {/* Bottle body */}
      <div style={s.bottleBody}>
        <div style={s.bottleLabelArea}>
          <PillSVG shape={bottle.pillShape} color={bottle.pillColor} size={32} />
        </div>
      </div>
      {/* Medicine name */}
      <span style={s.bottleName}>{bottle.name}</span>
    </button>
  )
}

// ── Guideline table ────────────────────────────────────────────

function GuidelineTable({ medicines, t }: { medicines: Medicine[]; t: ReturnType<typeof useTranslation>['t'] }) {
  const pb = t.pillbox
  return (
    <div style={s.tableScroll}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.thMed}>{pb.columnMed}</th>
            {SCHEDULES.map(sch => (
              <th key={sch} style={s.thSched}>
                <div style={s.schedHead}>
                  <span style={s.schedHeadIcon}>{SCHEDULE_ICONS[sch]}</span>
                  <span style={s.schedHeadLabel}>{pb.schedules[sch]}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {medicines.map((med, i) => (
            <tr key={med.id} style={i % 2 === 0 ? s.trEven : s.trOdd}>
              <td style={s.tdMed}>
                <div style={s.tdMedInner}>
                  <PillSVG shape={med.pillShape} color={med.pillColor} size={22} />
                  <span>{med.name}</span>
                </div>
              </td>
              {SCHEDULES.map(sch => (
                <td key={sch} style={{
                  ...s.tdCount,
                  color:      med.dosage[sch] === 0 ? '#C0B8A8' : '#2E1F14',
                  fontWeight: med.dosage[sch] > 0 ? 700 : 400,
                }}>
                  {med.dosage[sch]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Compartment ────────────────────────────────────────────────

function Compartment({ schedule, pills, isWrong, isCorrect, hasBottleSelected, onDrop, onRemovePill, t }: {
  schedule:          Schedule
  pills:             PlacedPill[]
  isWrong:           boolean
  isCorrect:         boolean
  hasBottleSelected: boolean
  onDrop:            () => void
  onRemovePill:      (pillId: string) => void
  t:                 ReturnType<typeof useTranslation>['t']
}) {
  const theme = COMPARTMENT_THEME[schedule]

  // Feedback overrides body background; header stays branded
  const bodyBg = isWrong ? '#FFF0F0' : isCorrect ? '#F0FFF4' : theme.body
  const outlineColor = isWrong ? '#E63946' : isCorrect ? '#2ECC71' : hasBottleSelected ? theme.border : 'transparent'

  return (
    <div
      style={{
        ...s.compartment,
        outline:    `3px solid ${outlineColor}`,
        background: bodyBg,
      }}
      onClick={(e) => { e.stopPropagation(); if (hasBottleSelected) onDrop() }}
    >
      {/* Coloured header band */}
      <div style={{ ...s.compartmentHeader, background: theme.header }}>
        <span style={s.compartmentIcon}>{SCHEDULE_ICONS[schedule]}</span>
        <span style={{ ...s.compartmentLabel, color: theme.headerText }}>
          {t.pillbox.schedules[schedule]}
        </span>
        {isWrong   && <span style={s.wrongBadge}>✗</span>}
        {isCorrect && <span style={s.correctBadge}>✓</span>}
      </div>

      {/* Pills area */}
      <div style={s.pillsArea}>
        {pills.map(pill => (
          <button
            key={pill.id}
            style={s.pillBtn}
            onClick={(e) => { e.stopPropagation(); onRemovePill(pill.id) }}
            aria-label="Quitar pastilla"
            title="Quitar pastilla"
          >
            <PillSVG shape={pill.pillShape} color={pill.pillColor} size={26} />
          </button>
        ))}
        {pills.length === 0 && (
          <span style={s.emptySlotHint}>
            {hasBottleSelected ? '+ añadir' : '—'}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Setup screen ───────────────────────────────────────────────

function SetupScreen({ difficulty, onDifficultyChange, onStart, onBack }: {
  difficulty:         Difficulty
  onDifficultyChange: (d: Difficulty) => void
  onStart:            () => void
  onBack:             () => void
}) {
  const { t } = useTranslation()
  const pb = t.pillbox

  const diffOptions = DIFFICULTIES.map(d => ({
    id:    d,
    label: pb.difficulty[d].label,
    hint:  pb.difficulty[d].hint,
  }))

  return (
    <GameSetupScreen
      title={pb.title}
      subtitle={pb.subtitle}
      backLabel={pb.backToDashboard}
      onBack={onBack}
      onStart={onStart}
      startLabel={pb.startBtn}
      difficulties={diffOptions}
      selectedDifficulty={difficulty}
      onDifficultyChange={(d) => onDifficultyChange(d as Difficulty)}
      chooseDifficultyLabel={pb.chooseDifficultyTitle}
      preview={<PillboxPreview />}
    />
  )
}

function PillboxPreview() {
  return (
    <div style={s.preview}>
      <div style={s.previewRow}>
        {(SCHEDULES).map(sch => (
          <div key={sch} style={s.previewBox}>
            <div style={{ ...s.previewHeader, background: COMPARTMENT_THEME[sch].header }}>
              <span>{SCHEDULE_ICONS[sch]}</span>
            </div>
            <div style={s.previewBody} />
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Play screen ────────────────────────────────────────────────

function PlayScreen({ state, onSelectBottle, onDeselect, onAddPill, onRemovePill, onCheck, onBack }: {
  state:           ReturnType<typeof usePillbox>['state']
  onSelectBottle:  (id: string) => void
  onDeselect:      () => void
  onAddPill:       (s: Schedule) => void
  onRemovePill:    (s: Schedule, pillId: string) => void
  onCheck:         () => void
  onBack:          () => void
}) {
  const { t } = useTranslation()
  const isMobile = useMobile()
  const timer    = useTimer(state.startTime, state.endTime)
  const pb       = t.pillbox

  const selectedBottle = state.bottles.find(b => b.id === state.selectedBottleId)

  // Progress: total pills placed vs total required
  const totalRequired = state.medicines.reduce((sum, med) =>
    sum + SCHEDULES.reduce((s, sch) => s + med.dosage[sch], 0), 0)
  const totalPlaced   = SCHEDULES.reduce((sum, sch) => sum + state.compartments[sch].length, 0)
  const progress      = totalRequired > 0 ? Math.min(totalPlaced / totalRequired, 1) : 0

  const instruction = selectedBottle
    ? pb.instructionPlace
    : pb.instructionSelect

  return (
    <div style={s.playRoot} onClick={onDeselect}>
      <div onClick={(e) => e.stopPropagation()}>
        <GamePlayHeader
          title={pb.title}
          stats={[
            { value: state.moves, label: pb.moves },
            { value: timer,       label: pb.time },
          ]}
          progress={progress * 100}
          progressNow={totalPlaced}
          progressMax={totalRequired}
          onBack={() => { onBack() }}
          backLabel={pb.backToDashboard}
        />
      </div>

      {/* Instruction */}
      <p style={{ ...s.instruction, ...(isMobile ? s.instructionMobile : {}) }}>
        {instruction}
      </p>

      {/* Guideline section */}
      <section style={s.section} onClick={(e) => e.stopPropagation()}>
        <h2 style={s.sectionTitle}>📋 {pb.guidelineTitle}</h2>
        <GuidelineTable medicines={state.medicines} t={t} />
      </section>

      {/* Pillbox section */}
      <section style={s.section}>
        <h2 style={s.sectionTitle}>💊 {pb.pillboxTitle}</h2>
        <div style={s.pillboxRow}>
          {SCHEDULES.map(sch => (
            <Compartment
              key={sch}
              schedule={sch}
              pills={state.compartments[sch]}
              isWrong={state.checked && state.wrongSchedules.includes(sch)}
              isCorrect={state.checked && !state.wrongSchedules.includes(sch)}
              hasBottleSelected={!!state.selectedBottleId}
              onDrop={() => onAddPill(sch)}
              onRemovePill={(pillId) => onRemovePill(sch, pillId)}
              t={t}
            />
          ))}
        </div>
      </section>

      {/* Bottle shelf */}
      <section style={s.section}>
        <h2 style={s.sectionTitle}>🧴 {pb.bottlesTitle}</h2>
        <div style={s.bottleShelf}>
          {state.bottles.map(bottle => (
            <BottleCard
              key={bottle.id}
              bottle={bottle}
              isSelected={bottle.id === state.selectedBottleId}
              onSelect={() => onSelectBottle(bottle.id)}
            />
          ))}
        </div>
      </section>

      {/* Check section */}
      <div style={s.checkSection} onClick={(e) => e.stopPropagation()}>
        {state.checked && state.wrongSchedules.length > 0 && (
          <p style={s.wrongFeedback}>{pb.wrongFeedback}</p>
        )}
        <button style={s.checkBtn} onClick={onCheck}>
          {pb.checkBtn}
        </button>
      </div>
    </div>
  )
}

// ── Root component ─────────────────────────────────────────────

export default function PillboxGame({ onBack }: { onBack: () => void }) {
  const { t }        = useTranslation()
  const sessionMode  = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<PillboxConfig>('pillbox', !sessionMode)
  const restored = savedSession?.config ?? null

  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : (restored ? 'playing' : 'setup'))
  const [difficulty, setDifficulty] = useState<Difficulty>((sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy')

  const hook = usePillbox(difficulty)

  const { gameComplete, moves, startTime, endTime, medicines } = hook.state

  const medicinePairs = medicines.reduce((sum, med) =>
    sum + SCHEDULES.filter(sch => med.dosage[sch] > 0).length, 0)

  const { sessionId } = useGameSession({
    activitySlug: 'pillbox',
    difficulty,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug:  'pillbox',
    difficulty,
    moves,
    startTime,
    endTime,
    pairsTotal:   medicinePairs,
    pairsMatched: medicinePairs,
    sessionId,
    onCompleted:  () => { clear(); setScreen('complete') },
  })

  const handleStart = () => {
    hook.resetGame()
    saveConfig({ difficulty })
    setScreen('playing')
  }

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}
      {screen === 'setup' && (
        <SetupScreen
          difficulty={difficulty}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
        />
      )}

      {screen === 'playing' && (
        <PlayScreen
          state={hook.state}
          onSelectBottle={hook.selectBottle}
          onDeselect={hook.deselectBottle}
          onAddPill={hook.addPillToCompartment}
          onRemovePill={hook.removePillFromCompartment}
          onCheck={hook.checkAnswer}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
        />
      )}

      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="💊"
          title={t.pillbox.complete.title}
          subtitle={t.pillbox.complete.subtitle}
          stats={[
            { value: String(moves), label: t.pillbox.complete.movesLabel },
            { value: formatGameTime(startTime, endTime), label: t.pillbox.complete.timeLabel },
            { value: t.pillbox.difficulty[difficulty].label, label: t.pillbox.complete.levelLabel },
          ]}
          playAgainLabel={t.pillbox.complete.playAgain}
          changeLevelLabel={t.pillbox.complete.changeLevel}
          backLabel={t.pillbox.complete.backToDashboard}
          onPlayAgain={handleStart}
          onChangeLevel={() => { clear(); setScreen('setup') }}
          onBack={onBack}
        />
      )}
    </div>
  )
}

// ── Styles ─────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:  '100vh',
    background: COLORS.bgPage,
  },

  // ── Play screen ──
  playRoot: {
    minHeight:      '100vh',
    paddingBottom:  24,
    background:     COLORS.bgPage,
    cursor:         'default',
  },



  // Instruction
  instruction: {
    textAlign:    'center',
    fontStyle:    'italic',
    fontSize:     '1.125rem',
    color:        '#6B4C3B',
    padding:      '10px 20px 4px',
  },
  instructionMobile: {
    fontSize: '1.0625rem',
  },

  // Section
  section: {
    padding: '16px 16px 0',
  },
  sectionTitle: {
    fontSize:     '1rem',
    fontWeight:   700,
    color:        '#2E1F14',
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },

  // Guideline table
  tableScroll: {
    overflowX:     'auto',
    borderRadius:  10,
    border:        '1px solid #E8E0D0',
    background:    '#FFFFFF',
  },
  table: {
    width:           '100%',
    borderCollapse:  'collapse',
    minWidth:        280,
  },
  thMed: {
    padding:      '12px 14px',
    textAlign:    'left',
    fontSize:     '0.9375rem',
    fontWeight:   700,
    color:        '#6B4C3B',
    background:   '#FBF7EF',
    borderBottom: '1px solid #E8E0D0',
    whiteSpace:   'nowrap',
  },
  thSched: {
    padding:      '8px 6px',
    textAlign:    'center',
    fontSize:     '0.875rem',
    fontWeight:   700,
    color:        '#6B4C3B',
    background:   '#FBF7EF',
    borderBottom: '1px solid #E8E0D0',
    minWidth:     58,
  },
  schedHead: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           3,
  },
  schedHeadIcon: {
    fontSize: '1.125rem',
  },
  schedHeadLabel: {
    fontSize:  '0.875rem',
    fontWeight: 700,
    color:     '#6B4C3B',
  },
  trEven: { background: '#FFFFFF' },
  trOdd:  { background: '#FBF9F6' },
  tdMed: {
    padding:      '12px 14px',
    fontSize:     '1rem',
    fontWeight:   600,
    color:        '#2E1F14',
    borderBottom: '1px solid #F0E8D8',
    whiteSpace:   'nowrap',
  },
  tdMedInner: {
    display:    'flex',
    alignItems: 'center',
    gap:        10,
  },
  tdCount: {
    padding:      '12px 8px',
    textAlign:    'center',
    fontSize:     '1.375rem',
    borderBottom: '1px solid #F0E8D8',
  },

  // Pillbox horizontal row
  pillboxRow: {
    display:         'flex',
    flexDirection:   'row',
    gap:             0,
    borderRadius:    16,
    overflow:        'hidden',
    border:          '2px solid #D8D0C4',
    background:      '#FFFFFF',
    boxShadow:       '0 4px 16px rgba(0,0,0,0.08)',
  },

  // Compartment (flex child, equal width)
  compartment: {
    flex:          1,
    display:       'flex',
    flexDirection: 'column',
    minHeight:     150,
    borderRight:   '1px solid #E8E0D0',
    cursor:        'pointer',
    transition:    'outline 0.15s, background 0.15s',
    outline:       '0px solid transparent',
    outlineOffset: '-2px',
  },
  compartmentHeader: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            3,
    padding:        '10px 6px 8px',
    // background set inline per schedule
  },
  compartmentIcon: {
    fontSize: '1.375rem',
  },
  compartmentLabel: {
    fontSize:   '0.875rem',
    fontWeight: 800,
    textAlign:  'center',
    lineHeight: 1.1,
    // color set inline (white on colored header)
  },
  wrongBadge: {
    fontSize:     '0.875rem',
    fontWeight:   800,
    color:        '#FFFFFF',
    background:   '#E63946',
    borderRadius: 20,
    padding:      '2px 7px',
    marginTop:    2,
  },
  correctBadge: {
    fontSize:     '0.875rem',
    fontWeight:   800,
    color:        '#FFFFFF',
    background:   COLORS.green,
    borderRadius: 20,
    padding:      '2px 7px',
    marginTop:    2,
  },
  pillsArea: {
    flex:         1,
    display:      'flex',
    flexWrap:     'wrap',
    gap:          4,
    padding:      '8px 6px',
    alignContent: 'flex-start',
    alignItems:   'flex-start',
  },
  pillBtn: {
    background:     'transparent',
    border:         'none',
    cursor:         'pointer',
    padding:        2,
    minWidth:       32,
    minHeight:      32,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    borderRadius:   6,
  },
  emptySlotHint: {
    fontSize:  '0.875rem',
    color:     '#C0B8A8',
    fontStyle: 'italic',
    padding:   '4px 0',
    width:     '100%',
    textAlign: 'center',
  },

  // Bottle shelf
  bottleShelf: {
    display:         'flex',
    flexDirection:   'row',
    gap:             10,
    overflowX:       'auto',
    paddingBottom:   8,
  },

  // Bottle card
  bottle: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            0,
    border:         '2px solid #D8D0C4',
    borderRadius:   12,
    padding:        '0 0 10px',
    background:     '#FFFFFF',
    minWidth:       90,
    maxWidth:       100,
    cursor:         'pointer',
    flexShrink:     0,
    transition:     'border-color 0.15s, box-shadow 0.15s',
    overflow:       'hidden',
  },
  bottleCap: {
    width:          '55%',
    height:         14,
    borderRadius:   '3px 3px 0 0',
    background:     '#888',
    marginBottom:   0,
  },
  bottleNeck: {
    width:          '38%',
    height:         10,
    background:     '#E8E0D0',
  },
  bottleBody: {
    width:          '100%',
    padding:        '8px 6px',
    display:        'flex',
    justifyContent: 'center',
  },
  bottleLabelArea: {
    border:         '1px dashed #D0C8B8',
    borderRadius:   6,
    padding:        '6px 8px',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     '#FBF7EF',
    width:          '100%',
  },
  bottleName: {
    fontSize:     '0.875rem',
    fontWeight:   700,
    color:        '#2E1F14',
    textAlign:    'center',
    padding:      '0 6px',
    maxWidth:     90,
    overflow:     'hidden',
    textOverflow: 'ellipsis',
    whiteSpace:   'nowrap',
  },

  // Check section
  checkSection: {
    padding:   '16px 16px 8px',
    display:   'flex',
    flexDirection: 'column',
    gap:       8,
  },
  checkBtn: {
    width:        '100%',
    minHeight:    56,
    fontSize:     '1.125rem',
    fontWeight:   700,
    color:        '#FFFFFF',
    background:   COLORS.gradientPrimary,
    border:       'none',
    borderRadius: 14,
    cursor:       'pointer',
    letterSpacing: '0.02em',
  },
  wrongFeedback: {
    textAlign:    'center',
    fontSize:     '1.0625rem',
    fontWeight:   600,
    color:        '#E63946',
    background:   '#FFF0F0',
    border:       '1px solid #FFCDD2',
    borderRadius: 10,
    padding:      '10px 14px',
  },

  // ── Setup preview ──
  preview: {
    display:        'flex',
    justifyContent: 'center',
    padding:        '12px 0',
  },
  previewRow: {
    display:      'flex',
    flexDirection: 'row',
    borderRadius: 12,
    overflow:     'hidden',
    border:       '2px solid #D8D0C4',
    boxShadow:    '0 3px 10px rgba(0,0,0,0.08)',
  },
  previewBox: {
    width:         56,
    display:       'flex',
    flexDirection: 'column',
    borderRight:   '1px solid #E8E0D0',
  },
  previewHeader: {
    height:         36,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontSize:       '1.125rem',
  },
  previewBody: {
    height:     32,
    background: '#FAFAF8',
  },

  // ── Complete screen ──
  completeRoot: {
    minHeight:      '100vh',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        24,
    background:     COLORS.bgPage,
  },
  completeCard: {
    background:   '#FFFFFF',
    borderRadius: 24,
    padding:      '32px 24px',
    maxWidth:     400,
    width:        '100%',
    textAlign:    'center',
    boxShadow:    '0 8px 32px rgba(27,67,50,0.12)',
  },
  completeEmoji: {
    fontSize:     '3.5rem',
    marginBottom: 8,
  },
  completeTitle: {
    fontSize:     '1.625rem',
    fontWeight:   800,
    color:        COLORS.greenDark,
    marginBottom: 6,
  },
  completeSubtitle: {
    fontSize:     '1rem',
    color:        '#6B4C3B',
    marginBottom: 20,
  },
  statsGrid: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap:                 10,
    marginBottom:        24,
  },
  statItem: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    background:     '#F5F0E8',
    borderRadius:   12,
    padding:        '10px 8px',
  },
  statValue: {
    fontSize:   '1.25rem',
    fontWeight: 800,
    color:      COLORS.greenDark,
  },
  statLabel: {
    fontSize:  '0.875rem',
    color:     '#8B7355',
    marginTop: 2,
  },
  completeBtns: {
    display:       'flex',
    flexDirection: 'column',
    gap:           10,
  },
  btnPrimary: {
    minHeight:    52,
    fontSize:     '1.0625rem',
    fontWeight:   700,
    color:        '#FFFFFF',
    background:   COLORS.gradientPrimary,
    border:       'none',
    borderRadius: 12,
    cursor:       'pointer',
  },
  btnSecondary: {
    minHeight:    52,
    fontSize:     '1rem',
    fontWeight:   700,
    color:        COLORS.greenDark,
    background:   '#D4EDDA',
    border:       '2px solid #B7E4C7',
    borderRadius: 12,
    cursor:       'pointer',
  },
  btnGhost: {
    minHeight:    48,
    fontSize:     '0.9375rem',
    fontWeight:   600,
    color:        '#6B4C3B',
    background:   'transparent',
    border:       '1px solid #D8D0C4',
    borderRadius: 12,
    cursor:       'pointer',
  },
}
